import { boundMethod } from 'autobind-decorator';
import {
  Container,
  InteractionEvent,
  InteractionManager,
  Sprite,
} from 'pixi.js';
import { createElement } from 'react';

import { TooltipContent } from 'flow-builder/components/connectorTooltip/connectorTooltip';

import type { CanvasComponent } from '../../types/canvasComponent';
import { HighlightType } from '../../types/node';
import { getIsNodeHighlighted } from '../../utils/node/getIsNodeHighlighted/getIsNodeHighlighted';
import {
  createTextureSpriteMap,
  HighlightSpriteMap,
  HighlightTextureMap,
  setHighlightSpriteVisibility,
} from '../utils/highlightTextureMap';

import { styles } from './nodeArrowStyles';
import type { Props } from './nodeComponent';
import { styles as nodeStyles } from './nodeStyles';

export class NodeArrow implements CanvasComponent<Props> {
  public props: Props;
  public ref: Container;

  public isLocked = false;
  public isHighlighted = false;

  public arrow: Container;
  public disconnect: Container;
  public locked: Container;

  private _arrowTypes: HighlightSpriteMap;
  private _disconnectTypes: HighlightSpriteMap;
  private _lockedTypes: HighlightSpriteMap;

  static InitializeTextureSprite(
    textureMap: HighlightTextureMap,
    name: string,
  ): HighlightSpriteMap {
    return Object.fromEntries(
      Object.entries(textureMap).map(([type, texture]) => {
        const sprite = new Sprite(texture);

        sprite.visible = false;
        sprite.anchor.set(0.5, 0.5);
        sprite.name = `${name} (${type})`;

        return [type, sprite];
      }),
    ) as HighlightSpriteMap;
  }

  constructor(props: Props) {
    this.props = props;

    const { arrowTextures } = this.props;

    const ref = new Container();
    const arrow = new Container();
    const disconnect = new Container();
    const locked = new Container();

    this._arrowTypes = createTextureSpriteMap(arrowTextures.arrow, 'Arrow');
    this._disconnectTypes = createTextureSpriteMap(
      arrowTextures.disconnect,
      'Disconnect',
    );
    this._lockedTypes = createTextureSpriteMap(arrowTextures.locked, 'Locked');

    ref.position.set(nodeStyles.base.width / 2, 0);

    const addSpriteToContainer = (sprite: Sprite, container: Container) =>
      container.addChild(sprite);
    const addSpriteTypes = (types: HighlightSpriteMap, container: Container) =>
      Object.values(types).forEach((sprite) =>
        addSpriteToContainer(sprite, container),
      );

    addSpriteTypes(this._arrowTypes, arrow);
    addSpriteTypes(this._disconnectTypes, disconnect);
    addSpriteTypes(this._lockedTypes, locked);

    const assignContainerAttributes = (container: Container) => {
      container.interactive = true;
      container.buttonMode = true;
    };

    assignContainerAttributes(arrow);
    assignContainerAttributes(disconnect);
    assignContainerAttributes(locked);

    disconnect.visible = false;
    locked.visible = false;

    ref.addChild(arrow);
    ref.addChild(disconnect);
    ref.addChild(locked);

    this.ref = ref;
    this.arrow = arrow;
    this.disconnect = disconnect;
    this.locked = locked;

    this.isLocked = this.getIsLocked();
    this.isHighlighted = this.getIsHighlighted();

    this.drawArrowHighlight();
    this.drawArrowVisibility();
    this.addDisconnectListener();
  }

  componentWillUnmount() {
    this.unmount();
  }

  private getIsLocked() {
    const {
      node: { id },
      editorContext: {
        state: {
          nodeState: { scopeVars },
        },
      },
    } = this.props;

    return Object.values(scopeVars).some(
      ({ ancestors }) => ancestors && ancestors.indexOf(id) >= 0,
    );
  }

  private getIsHighlighted() {
    const {
      node,
      editorContext: {
        state: { highlightState },
      },
    } = this.props;

    return getIsNodeHighlighted(node.id, highlightState.nodes, true);
  }

  @boundMethod
  private mouseOutListener(event: InteractionEvent) {
    const {
      editorContext: { view },
    } = this.props;

    if (!view.isReady) {
      return;
    }

    const arrow = this.arrow;
    const disconnect = this.disconnect;
    const locked = this.locked;

    const { plugins } = view.app.renderer;
    const interaction = plugins.interaction as InteractionManager;
    const point = event.data.global;

    const target = this.isLocked ? locked : disconnect;

    const isHoveringElement = !!interaction.hitTest(point, target);

    if (!isHoveringElement) {
      if (this.isLocked) {
        this.onLockOut();
      }

      target.visible = false;
      arrow.visible = true;

      this.removeMouseOutListener();
    }
  }

  private handleMouseOutListener(method: 'addListener' | 'removeListener') {
    const {
      editorContext: { view },
    } = this.props;

    if (!view.isReady) {
      return;
    }

    view.viewport[method]('mousemove', this.mouseOutListener);
  }

  private addMouseOutListener() {
    this.handleMouseOutListener('addListener');
  }

  private removeMouseOutListener() {
    this.handleMouseOutListener('removeListener');
  }

  private addDisconnectListener() {
    const arrow = this.arrow;
    const disconnect = this.disconnect;
    const locked = this.locked;

    arrow.on('mouseover', () => {
      if (this.isLocked) {
        locked.visible = true;
      } else {
        disconnect.visible = true;
      }

      arrow.visible = false;
      this.addMouseOutListener();
    });

    locked.on('click', this.onLockClick);
    disconnect.on('click', this.onDisconnectClick);
  }

  @boundMethod
  private onLockClick(event: InteractionEvent) {
    event.stopPropagation();

    const {
      node: { id },
      editorContext,
    } = this.props;
    const {
      state: {
        nodeState: { scopeVars },
      },
      handlers: {
        ui: { onTooltipShow },
      },
    } = editorContext;
    const { target } = event;

    const { a, tx, ty } = target.worldTransform;

    const requiredScopeVar = Object.entries(scopeVars).find(
      ([_id, { ancestors }]) => ancestors && ancestors.indexOf(id) >= 0,
    );

    if (!requiredScopeVar) {
      return;
    }

    const [dependantId, { needs }] = requiredScopeVar;
    const isDependant = dependantId === id;

    onTooltipShow({
      head: 'Attention',
      content: createElement(TooltipContent, { isDependant, needs }),
      position: {
        x: tx,
        y: ty - styles._lockedCircle.radius * a,
      },
    });
  }

  @boundMethod
  private onLockOut() {
    const {
      editorContext: {
        handlers: {
          ui: { onTooltipHide },
        },
      },
    } = this.props;

    onTooltipHide();
  }

  @boundMethod
  private onDisconnectClick(event: InteractionEvent) {
    event.stopPropagation();
    this.removeMouseOutListener();

    const container = this.disconnect;
    const {
      node: { id },
      editorContext: {
        handlers: {
          node: { onDisconnect },
        },
      },
    } = this.props;

    const disconnect = onDisconnect(id);

    container.visible = false;
    disconnect();
  }

  private drawArrowVisibility() {
    const {
      node: { parentId },
    } = this.props;
    const arrow = this.arrow;

    if (!parentId) {
      arrow.visible = false;
      return;
    }

    arrow.visible = true;
  }

  private drawArrowHighlight() {
    const { editorContext } = this.props;
    const {
      state: { highlightState },
    } = editorContext;

    const activeHighlightType = this.isHighlighted
      ? highlightState.type
      : HighlightType.NONE;

    setHighlightSpriteVisibility(this._arrowTypes, activeHighlightType);
    setHighlightSpriteVisibility(this._disconnectTypes, activeHighlightType);
    setHighlightSpriteVisibility(this._lockedTypes, activeHighlightType);
  }

  public isChanged(props: Props): boolean {
    const {
      node,
      node: { parentId },
      editorContext: {
        state: { highlightState },
      },
    } = props;
    const {
      node: propNode,
      node: { parentId: propsParentId },
      editorContext: {
        state: { highlightState: propHighlightState },
      },
    } = this.props;

    const isHighlighted = getIsNodeHighlighted(
      node.id,
      highlightState.nodes,
      true,
    );
    const propIsHighlighted = getIsNodeHighlighted(
      propNode.id,
      propHighlightState.nodes,
      true,
    );

    const isNodeHighlightDifferent = isHighlighted !== propIsHighlighted;
    const isParentIdDifferent = parentId !== propsParentId;

    return isNodeHighlightDifferent || isParentIdDifferent;
  }

  @boundMethod
  public redraw(props: Props): void {
    this.updatePropsWithoutRedraw(props);

    this.drawArrowHighlight();
    this.drawArrowVisibility();
  }

  @boundMethod
  public updatePropsWithoutRedraw(props: Props): void {
    this.props = props;
    this.isLocked = this.getIsLocked();
    this.isHighlighted = this.getIsHighlighted();
  }

  @boundMethod
  public unmount(): void {
    this.ref.destroy();
    this.arrow.destroy();
    this.disconnect.destroy();
    this.locked.destroy();
    this.removeMouseOutListener();
  }
}
